import { Button } from './Button'

export interface EmptyStateProps {
  emoji: string
  title: string
  description: string
  action?: {
    label: string
    onClick: () => void
    variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  }
  className?: string
}

/**
 * Reusable empty state component for consistent UX
 *
 * Use this component when a screen/section has no data to display.
 * Provides clear visual feedback and optional CTA to help users proceed.
 *
 * @example
 * // Recipe history - no recipes yet
 * <EmptyState
 *   emoji="⭐"
 *   title="No Recipe History Yet"
 *   description="Your favorites and past recipes will appear here after your first meal plan"
 *   action={{
 *     label: 'Create Your First Plan →',
 *     onClick: () => navigate('/planning')
 *   }}
 * />
 *
 * // Calendar - no meals scheduled
 * <EmptyState
 *   emoji="📅"
 *   title="No Meals Scheduled"
 *   description="Plan meals and schedule them on your calendar"
 *   action={{
 *     label: 'Plan Meals →',
 *     onClick: handleStartPlanning,
 *     variant: 'primary'
 *   }}
 * />
 */
export function EmptyState({
  emoji,
  title,
  description,
  action,
  className = ''
}: EmptyStateProps) {
  return (
    <div className={`text-center py-12 px-6 ${className}`}>
      {/* Large emoji icon */}
      <div
        className="text-6xl mb-4"
        role="img"
        aria-label={title}
      >
        {emoji}
      </div>

      {/* Title */}
      <h3 className="text-xl font-semibold text-gray-900 mb-2">
        {title}
      </h3>

      {/* Description */}
      <p className="text-gray-600 mb-6 max-w-md mx-auto">
        {description}
      </p>

      {/* Optional action button */}
      {action && (
        <Button
          variant={action.variant || 'primary'}
          size="lg"
          onClick={action.onClick}
        >
          {action.label}
        </Button>
      )}
    </div>
  )
}

export default EmptyState
